import {omit} from 'lodash';

import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {
  CardNoteUpdateAction,
  createNote,
  deleteNote,
  updateNote,
} from '../../../helpers/models/cardNoteHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {ApiCardNote, ApiCardNotes, CardNoteModel} from '../../../models/api/cardNoteModel';
import {
  ApiSendOptions,
  deleteAsync,
  fetchApiAsync,
  postApiAsync,
  putApiAsync,
} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {findCardNoteLock} from '../../../services/locks/resourceLocks';
import {Dispatch} from '../../appStore';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {ResourceMap} from '../../appTypes';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {showContactDetailsUndoBannerAction} from '../../panels/app/banners/bannersActions';
import {ContactDetailsPanelId} from '../../panels/panelStateBase';
import {updateDataAction} from '../dataGroupsActions';
import {DataState} from '../dataState';

/*
 * Action types.
 */

export enum CardNotesActionsEnum {
  NOTE_DELETED = '@@data/cardNotes/NOTE_DELETED',
  NOTE_CREATED = '@@data/cardNotes/NOTE_CREATED',
  NOTE_UNDELETED = '@@data/cardNotes/NOTE_UNDELETED',
}

/*
 * Action Types.
 */

interface CardNoteActionPayload {
  note: CardNoteModel;
}

export interface CardNoteDeletedAction extends ActionWithPayload<CardNoteActionPayload> {
  type: CardNotesActionsEnum.NOTE_DELETED;
}
export interface CardNoteCreatedAction extends ActionWithPayload<CardNoteActionPayload> {
  type: CardNotesActionsEnum.NOTE_CREATED;
}

export interface CardNoteUndeletedAction extends ActionWithPayload<CardNoteActionPayload> {
  type: CardNotesActionsEnum.NOTE_UNDELETED;
}

/*
 * Actions.
 */

export function updateCardNoteAction(newCardNote: CardNoteModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing card note.
    const existingCardNote = getState().data.cardNotes[newCardNote.id];

    // No need to do anything if the new note is older.
    if (existingCardNote && existingCardNote.updatedAt > newCardNote.updatedAt) {
      return null;
    }

    // Otherwise, update the note.
    return dispatch(updateDataAction({cardNotes: [newCardNote], updatedAt: newCardNote.updatedAt}));
  };
}

export function fetchCardNotesAsyncAction(
  card: CardModel,
  options?: ApiSendOptions,
): ThunkAsyncAction<ReadonlyArray<CardNoteModel>> {
  return async (dispatch) => {
    const getNotesUrl = `${card.url}/card_notes`;

    const cardNotes = await fetchApiAsync(ApiCardNotes, getNotesUrl, options);
    dispatch(updateDataAction({cardNotes}));
    return cardNotes;
  };
}

export function updateCardNoteAsyncAction(
  currentNote: CardNoteModel,
  updatedNote: CardNoteModel,
  undoBannerPanelId?: ContactDetailsPanelId,
): ThunkAsyncAction {
  const noteId = updatedNote.id;
  // Optimistically show the note was saved just now.
  const optimisticUpdatedAt = Date.now();
  const optimisticUpdater = createObjectPropertyUpdater<DataState, 'cardNotes'>(
    'cardNotes',
    createResourceMapValueUpdater(
      noteId,
      createUpdater<CardNoteModel>((_note) => ({...updatedNote, updatedAt: optimisticUpdatedAt})),
    ),
  );

  return async (dispatch, getState) => {
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCardNoteLock(noteId).lockAsync(async () => {
          const {company} = getState().data;
          const updateCardNoteUrl = `${company.url}/card_notes/${noteId}`;

          try {
            const savedNote = await putApiAsync(ApiCardNote, updateCardNoteUrl, {...updatedNote});

            maybeShowUndoBanner(
              dispatch,
              savedNote.cardId,
              updateNote(currentNote, savedNote),
              undoBannerPanelId,
            );

            return updateDataAction({
              cardNotes: [savedNote],
            });
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }
            throw error;
          }
        }),
      ),
    );
  };
}

export function createCardNoteAsyncAction(
  cardId: number,
  details: {text: string; namespace: string},
  undoBannerPanelId?: ContactDetailsPanelId,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const createNewNoteUrl = `${company.url}/cards/${cardId}/card_notes`;

    try {
      const cardNote = await postApiAsync(ApiCardNote, createNewNoteUrl, {...details});

      maybeShowUndoBanner(dispatch, cardNote.cardId, createNote(cardNote), undoBannerPanelId);

      batch(() => {
        dispatch(
          updateDataAction({
            cardNotes: [cardNote],
          }),
        );
        dispatch(newCardNoteAction(cardNote));
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteCardNoteAsyncAction(
  note: CardNoteModel,
  undoBannerPanelId?: ContactDetailsPanelId,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'cardNotes'>(
      'cardNotes',
      createUpdater<ResourceMap<CardNoteModel>>((cardNotes) => omit(cardNotes, note.id)),
    );
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCardNoteLock(note.id).lockAsync(async () => {
          const {company} = getState().data;
          const deleteCardNoteUrl = `${company.url}/card_notes/${note.id}`;

          try {
            await deleteAsync(deleteCardNoteUrl);

            maybeShowUndoBanner(dispatch, note.cardId, deleteNote(note), undoBannerPanelId);

            return dispatch(deleteCardNoteAction(note));
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}

export function undeleteCardNoteAsyncAction(note: CardNoteModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const undeleteCardNoteUrl = `${company.url}/card_notes/${note.id}/undelete`;

    try {
      const undeletedNote = await putApiAsync(ApiCardNote, undeleteCardNoteUrl, {});

      batch(() => {
        dispatch(updateDataAction({cardNotes: [undeletedNote]}));
        dispatch(undeleteCardNoteAction(undeletedNote));
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteCardNoteAction(note: CardNoteModel): CardNoteDeletedAction {
  return {
    payload: {note},
    type: CardNotesActionsEnum.NOTE_DELETED,
  };
}

export function newCardNoteAction(note: CardNoteModel): CardNoteCreatedAction {
  return {
    payload: {note},
    type: CardNotesActionsEnum.NOTE_CREATED,
  };
}

export function undeleteCardNoteAction(note: CardNoteModel): CardNoteUndeletedAction {
  return {
    payload: {note},
    type: CardNotesActionsEnum.NOTE_UNDELETED,
  };
}

/*
 * Union type.
 */

export type CardNoteAction = CardNoteDeletedAction | CardNoteCreatedAction | CardNoteUndeletedAction;

/*
 * Helpers.
 */

function maybeShowUndoBanner(
  dispatch: Dispatch,
  cardId: number,
  action: CardNoteUpdateAction,
  undoBannerPanelId: ContactDetailsPanelId | undefined,
) {
  if (!undoBannerPanelId) {
    return;
  }

  dispatch(showContactDetailsUndoBannerAction(undoBannerPanelId, cardId, action));
}
